import React, { useState } from 'react';
import { Deal } from '../types/pipeline';
interface HeatMapCellProps {
  deals: Deal[];
  metric: 'count' | 'value';
  maxValue: number;
  teamMemberName: string;
  stage: string;
  onClick: () => void;
}
export function HeatMapCell({
  deals,
  metric,
  maxValue,
  teamMemberName,
  stage,
  onClick
}: HeatMapCellProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const value = metric === 'count' ? deals.length : deals.reduce((sum, d) => sum + d.amount, 0);
  const isEmpty = deals.length === 0;
  // Calculate color intensity (0-1)
  const intensity = maxValue > 0 ? value / maxValue : 0;
  // Determine if this is a bottleneck (top 20% of values)
  const isBottleneck = intensity > 0.8 && !isEmpty;
  const getBackgroundColor = () => {
    if (isEmpty) return 'bg-slate-50 border-slate-200';
    if (isBottleneck) {
      // Orange to red gradient for bottlenecks
      if (intensity > 0.95) return 'bg-gradient-to-br from-red-500 to-red-600 border-red-400';
      if (intensity > 0.85) return 'bg-gradient-to-br from-orange-500 to-red-500 border-orange-400';
      return 'bg-gradient-to-br from-orange-400 to-orange-500 border-orange-300';
    }
    // Blue gradient for normal distribution
    if (intensity > 0.75) return 'bg-blue-700 border-blue-600';
    if (intensity > 0.5) return 'bg-blue-500 border-blue-400';
    if (intensity > 0.25) return 'bg-blue-300 border-blue-200';
    return 'bg-blue-100 border-blue-100';
  };
  const getTextColor = () => {
    if (isEmpty) return 'text-slate-400';
    if (intensity > 0.5 || isBottleneck) return 'text-white';
    return 'text-slate-700';
  };
  const formatValue = (val: number) => {
    if (metric === 'value') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(val);
    }
    return val.toString();
  };
  const avgDaysInStage = deals.length > 0 ? Math.round(deals.reduce((sum, d) => sum + d.daysInStage, 0) / deals.length) : 0;
  return <div className="relative" data-id="element-1">
      <button onClick={onClick} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className={`
          w-full h-20 rounded-lg border-2 transition-all duration-200
          ${getBackgroundColor()}
          ${isEmpty ? 'border-dashed cursor-default' : 'hover:scale-105 hover:shadow-lg cursor-pointer'}
          ${getTextColor()}
          flex flex-col items-center justify-center p-2
        `} disabled={isEmpty} data-id="element-2">
        {!isEmpty && <>
            <div className="text-2xl font-bold mb-1" data-id="element-3">{formatValue(value)}</div>
            <div className="text-xs opacity-90" data-id="element-4">
              {deals.length} {deals.length === 1 ? 'deal' : 'deals'}
            </div>
          </>}
        {isEmpty && <div className="text-xs font-medium" data-id="element-5">—</div>}
      </button>

      {/* Tooltip */}
      {showTooltip && !isEmpty && <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 pointer-events-none" data-id="element-6">
          <div className="bg-slate-900 text-white rounded-lg shadow-xl p-4 text-sm" data-id="element-7">
            <div className="font-semibold mb-2 text-white" data-id="element-8">
              {teamMemberName} • {stage}
            </div>

            <div className="space-y-2 border-t border-slate-700 pt-2" data-id="element-9">
              <div className="flex justify-between" data-id="element-10">
                <span className="text-slate-300" data-id="element-11">Deals:</span>
                <span className="font-semibold" data-id="element-12">{deals.length}</span>
              </div>
              <div className="flex justify-between" data-id="element-13">
                <span className="text-slate-300" data-id="element-14">Total Value:</span>
                <span className="font-semibold" data-id="element-15">
                  {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                maximumFractionDigits: 1
              }).format(deals.reduce((sum, d) => sum + d.amount, 0))}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-16">
                <span className="text-slate-300" data-id="element-17">Avg Days:</span>
                <span className="font-semibold" data-id="element-18">{avgDaysInStage}d</span>
              </div>
            </div>

            {isBottleneck && <div className="mt-3 pt-2 border-t border-slate-700" data-id="element-19">
                <div className="flex items-center space-x-2 text-orange-300" data-id="element-20">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-21">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" data-id="element-22" />
                  </svg>
                  <span className="text-xs font-medium" data-id="element-23">
                    Potential Bottleneck
                  </span>
                </div>
              </div>}

            {deals.length > 0 && deals.length <= 5 && <div className="mt-3 pt-2 border-t border-slate-700" data-id="element-24">
                <div className="text-xs text-slate-400 mb-1" data-id="element-25">Deals:</div>
                <div className="space-y-1" data-id="element-26">
                  {deals.slice(0, 5).map(deal => <div key={deal.id} className="text-xs truncate text-slate-200" data-id="element-27">
                      • {deal.borrowerName} (
                      {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                maximumFractionDigits: 0
              }).format(deal.amount)}
                      )
                    </div>)}
                </div>
              </div>}

            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px" data-id="element-28">
              <div className="border-8 border-transparent border-t-slate-900" data-id="element-29"></div>
            </div>
          </div>
        </div>}
    </div>;
}